import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { waterQualityApi } from "../api/waterQualityApi"
import { WaterQualityForm } from "../components/WaterQualityForm"
import { FormPageLayout } from "@/shared/components/forms"
import { PageLoading } from "@/shared/components/ui/LoadingSpinner"
import { Card } from "@/shared/components/ui/Card"
import { Button } from "@/shared/components/ui/Button"
import { useFarmFromUrl } from "@/shared/hooks/useUrlSync"
import { pondsApi } from "@/features/ponds/api/pondsApi"
import { queryKeys } from "@/shared/api/queryClient"
import { buildRoute } from "@/shared/lib/constants"
import { toApiRequest, type WaterQualityFormData } from "../schemas/waterQualitySchema"

export function EditWaterQualityPage() {
  const { testId } = useParams<{ testId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()

  const { farmId, isLoading: isFarmLoading } = useFarmFromUrl()
  const pondId = searchParams.get("pondId")

  // Fetch pond data using query params
  const { data: pond, isLoading: isPondLoading } = useQuery({
    queryKey: queryKeys.ponds.detail(farmId || "", pondId || ""),
    queryFn: () => pondsApi.get(farmId!, pondId!),
    enabled: !!farmId && !!pondId,
  })

  // Fetch existing test data
  const { data: test, isLoading: isTestLoading } = useQuery({
    queryKey: queryKeys.waterQuality.detail(farmId || "", pondId || "", testId || ""),
    queryFn: () => waterQualityApi.get(farmId!, pondId!, testId!),
    enabled: !!farmId && !!pondId && !!testId,
  })

  const updateMutation = useMutation({
    mutationFn: (data: WaterQualityFormData) => {
      if (!farmId || !pondId || !testId) throw new Error("Farm, pond, and test required")
      return waterQualityApi.update(farmId, pondId, testId, toApiRequest(data))
    },
    onSuccess: () => {
      // Invalidate both list and detail queries
      if (pondId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.waterQuality.all(pondId) })
      }
      if (farmId && pondId && testId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.waterQuality.detail(farmId, pondId, testId),
        })
      }
      handleCancel()
    },
  })

  const handleCancel = () => {
    if (farmId && pondId && testId) {
      navigate(buildRoute.waterQualityTest(farmId, pondId, testId))
    } else if (farmId) {
      navigate(buildRoute.waterQuality(farmId, pondId))
    }
  }

  const handleSubmit = async (data: WaterQualityFormData) => {
    await updateMutation.mutateAsync(data)
  }

  if (isFarmLoading || isPondLoading || isTestLoading) {
    return <PageLoading message="Loading test data..." />
  }

  if (!test) {
    return (
      <Card>
        <div className="text-center py-16">
          <h3 className="font-roboto text-base font-semibold text-gray-900">
            Test not found
          </h3>
          <p className="font-roboto text-sm text-gray-600 mt-2">
            The water quality test you're trying to edit doesn't exist.
          </p>
          <Button onClick={handleCancel} className="mt-4">
            Back to Water Quality
          </Button>
        </div>
      </Card>
    )
  }

  // Convert API response to form default values
  const defaultValues: Partial<WaterQualityFormData> = {
    dissolvedOxygen: test.dissolvedOxygen,
    ph: test.ph,
    temperature: test.temperature,
    testedAt: new Date(test.testedAt),
    salinity: test.salinity ?? undefined,
    ammonia: test.ammonia ?? undefined,
    nitrite: test.nitrite ?? undefined,
    nitrate: test.nitrate ?? undefined,
    turbidity: test.turbidity ?? undefined,
    notes: test.notes ?? "",
    imageIds: test.imageIds ?? [],
  }

  return (
    <FormPageLayout
      title="Edit Water Quality Test"
      subtitle={pond?.name}
      onCancel={handleCancel}
      onSubmit={() => {
        document.getElementById("water-quality-form")?.dispatchEvent(
          new Event("submit", { cancelable: true, bubbles: true })
        )
      }}
      isSubmitting={updateMutation.isPending}
      submitLabel="Save Changes"
      cancelLabel="Cancel"
    >
      <WaterQualityForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        isLoading={updateMutation.isPending}
      />
    </FormPageLayout>
  )
}
